import { useCallback, useMemo } from 'react';
import { useSelector } from '@xstate/react';
import { type Prediction } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { type LocationSearchService } from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const usePredictions = (params: Params) => {
  const { locationSearchService } = params;

  // ─── Derived Data ──────────────────────────────────────────────────────────

  const predictions = useSelector(
    locationSearchService,
    (snapshot) => snapshot.context.predictions ?? [],
  );

  const predictionQuery = useSelector(
    locationSearchService,
    (snapshot) => snapshot.context.predictionsSearchQuery ?? '',
  );

  const predictionLocations = useSelector(
    locationSearchService,
    (snapshot) => snapshot.context.predictionLocations,
  );

  const formattedPredictions = useMemo(
    () => predictions.map(formatPrediction),
    [predictions],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const selectPrediction = useCallback(
    (placeId: string) => {
      locationSearchService.send({ type: 'SELECT_PREDICTION', placeId });
    },
    [locationSearchService],
  );

  // ─── Flags ───────────────────────────────────────────────────────────────────

  const isEnabled = useFeatureFlag('CELS-4461-search-locations-by-predictions-enabled'); // prettier-ignore
  const isSurfaceDistanceForPredictionEnabled = useFeatureFlag('CELS-4064-surface-prediction-distance-on-location-cards-enabled'); // prettier-ignore

  const isFetchingPredictions = useSelector(
    locationSearchService,
    (snapshot) =>
      snapshot.matches('searchForPredictions.searching') ||
      snapshot.matches('searchForPredictions.selecting') ||
      (snapshot.matches('searchForPredictions.debouncing') &&
        snapshot.context.predictions.length === 0),
  );

  const pickupLocations = predictionLocations?.pickup ?? [];
  const outpostLocations = predictionLocations?.outpost ?? [];

  const hasPredictions =
    formattedPredictions.length > 0 ||
    pickupLocations.length > 0 ||
    outpostLocations.length > 0;

  const hasNoPredictions = Boolean(
    !isFetchingPredictions &&
      !hasPredictions &&
      predictionLocations &&
      predictions,
  );

  const shouldRenderPredictions =
    isEnabled &&
    (hasPredictions || hasNoPredictions || predictionQuery.length > 0);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isEnabled,
    isSurfaceDistanceForPredictionEnabled,
    isFetchingPredictions,
    formattedPredictions,
    predictionLocations,
    predictionQuery,
    hasPredictions,
    hasNoPredictions,
    shouldRenderPredictions,
    selectPrediction,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrediction(prediction: Prediction): FormattedPrediction {
  const { place_id, matched_substrings, description } = prediction;

  return {
    id: place_id,
    address: description,
    matchedSubstrings: matched_substrings,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  locationSearchService: LocationSearchService;
};

type FormattedPrediction = {
  id: string;
  address: string;
  name?: string;
  matchedSubstrings?: Prediction['matched_substrings'];
};
